/*MIT License

Copyright (c) 2021 Çağrı Aldemir

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.*/
import { useEffect, useState, useCallback, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { compare } from "compare-versions";
/* import { reloadApp } from "../shared/utils"; */

const CacheBuster = (props) => {
  const {
    children = null,
    currentVersion,
    isEnabled = false,
    isVerboseMode = false,
    appProvider,
    videoPlaying,
  } = props;

  const cacheInterval = appProvider.config.CACHE_INTERVAL ?? 60000;
  const appWillReload = useRef(false);
  const isVideoPlaying = useRef(false);

  const [cacheStatus, setCacheStatus] = useState({
    loading: true,
    isLatestVersion: false,
  });

  const log = useCallback(
    (message, isError) => {
      isVerboseMode && (isError ? console.error(message) : console.log(message));
    },
    [isVerboseMode]
  );

  const checkCacheStatus = useCallback(async () => {
    log("checkCacheStatus");
    try {
      const res = await fetch("/meta.json?" + new Date().getTime(), { cache: "no-store" });
      const { version: metaVersion } = await res.json();

      const shouldForceRefresh = isThereNewVersion(metaVersion, currentVersion);
      if (shouldForceRefresh && !isVideoPlaying.current) {
        log("There is a new version " + metaVersion + ". Should force refresh.");
        setCacheStatus({
          loading: false,
          isLatestVersion: false,
        });
      } else if (shouldForceRefresh && isVideoPlaying.current) {
        log("There is a new version " + metaVersion + ". Should force refresh but video is playing.");
        appWillReload.current = true;
      } else {
        log("There is no new version. No cache refresh needed.");
        setCacheStatus({
          loading: false,
          isLatestVersion: true,
        });
      }
    } catch (error) {
      log("An error occurred while checking cache status.", true);
      log(error, true);

      //Since there is an error, if isVerboseMode is false, the component is configured as if it has the latest version.
      !isVerboseMode &&
        setCacheStatus({
          loading: false,
          isLatestVersion: true,
        });
    }
  }, [currentVersion, isVerboseMode, log]);

  useEffect(() => {
    if (isEnabled) {
      setInterval(checkCacheStatus, cacheInterval);
    }
  }, [cacheInterval, checkCacheStatus, currentVersion, isEnabled, isVerboseMode]);

  useEffect(() => {
    if (videoPlaying.playing && appWillReload.current) {
      setCacheStatus({
        loading: false,
        isLatestVersion: false,
      });
    } else if (videoPlaying.playing) {
      isVideoPlaying.current = true;
    } else {
      isVideoPlaying.current = false;
    }
  }, [videoPlaying]);

  const isThereNewVersion = (metaVersion, currentVersion) => {
    return compare(metaVersion, currentVersion, ">");
  };

  const refreshCacheAndReload = async () => {
    try {
      if (window?.caches) {
        const { caches } = window;
        const cacheNames = await caches.keys();
        const cacheDeletions = cacheNames.map((n) => caches.delete(n));

        await Promise.allSettled(cacheDeletions);
        log("The cache has been deleted." + videoPlaying.playing);
        // To mitigate the issue of app reload creating TRAY/EPG call spikes in New Relic, we temporarily disable the reloadApp function. Code should not be removed.
        // TODO: Enable reloadApp function after TRAY/EPG call spikes issue is resolved.
        // reloadApp(appProvider);
      }
    } catch (error) {
      log("An error occurred while deleting the cache.", true);
      log(error, true);
    }
  };

  if (!isEnabled) {
    return children;
  } else {
    if (!cacheStatus.loading && !cacheStatus.isLatestVersion) {
      refreshCacheAndReload();
      return null;
    }
    return children;
  }
};

function mapStateToProps({ app }) {
  return {
    videoPlaying: app.videoPlaying,
    appProvider: app.provider,
  };
}

CacheBuster.propTypes = {
  children: PropTypes.element.isRequired,
  currentVersion: PropTypes.string.isRequired,
  isEnabled: PropTypes.bool.isRequired,
  isVerboseMode: PropTypes.bool,
  appProvider: PropTypes.object,
  videoPlaying: PropTypes.object,
};

export default connect(mapStateToProps)(CacheBuster);
